import { createSelector } from 'reselect';

import type { GlobalState } from '../App/types';

const getManageDocumentsState = (state: GlobalState) => state.manageDocuments;
const getApplicationProfileState = (
  state: GlobalState,
  customerType: ?string = null,
) =>
  customerType === 'resident'
    ? state.residentProfile
    : state.applicationProfile;
const getCurrentLocale = (state: GlobalState) => state.languageProvider.locale;

export const getDocumentTypesOptions = createSelector(
  [getManageDocumentsState, getCurrentLocale],
  ({ documentTypes }, locale) => {
    const seen = new Set();
    const documentTypesOptions = documentTypes
      .filter((doc) => {
        if (!doc.showForManualUploads || seen.has(doc.id)) return false;
        seen.add(doc.id);
        return true;
      })
      .map((documentType) => ({
        value: documentType.id,
        text: documentType.translations?.[locale] || documentType.name,
        disabled: false,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));

    return [
      {
        value: 'default',
        text: 'Choose an option',
        disabled: true,
      },
      ...documentTypesOptions,
    ];
  },
);
export const getApplicantsOptions = createSelector(
  [getApplicationProfileState],
  (profile: Object) => {
    const residentApp = profile?.applicationInfo ?? null;
    const applicantApp = profile?.currentApplication ?? null;
    if (!profile && (!residentApp || !applicantApp)) return [];
    const application = residentApp || applicantApp;
    const applicants = application?.applicants ?? [];
    const pendingApplicants = profile?.applicants?.pendingApplicants ?? [];

    const applicantsOptions = applicants
      .filter((applicant) => applicant?.resident?.status !== 'Prior')
      .map((applicant) => {
        if (applicant.applicantCustomer) {
          const customer = applicant.applicantCustomer.customer;
          return {
            value: applicant.id,
            text: `${customer.firstName} ${customer.lastName}`,
            disabled: false,
          };
        } else if (applicant.applicantPet) {
          const pet = applicant.applicantPet;
          return {
            value: applicant.id,
            text: `${pet.name}`,
            disabled: false,
          };
        } else {
          const minor = applicant.applicantMinor;
          const firstName = minor?.firstName ?? '';
          const lastName = minor?.lastName ?? '';
          return {
            value: applicant.id,
            text: `${firstName} ${lastName}`,
            disabled: false,
          };
        }
      });

    const existingIds = new Set(applicantsOptions.map((a) => a.value));

    const pendingApplicantsOptions = pendingApplicants
      .filter(
        (applicant) =>
          applicant.applicantCustomer?.customer?.status === 'Applicant' &&
          !existingIds.has(applicant.id),
      )
      .map((applicant) => ({
        value: applicant.id,
        text: applicant.name,
        disabled: false,
      }));

    return [
      {
        value: 'default',
        text: 'Choose an option',
        disabled: true,
      },
      ...applicantsOptions,
      ...pendingApplicantsOptions,
    ];
  },
);

export const getCurrentResidentState = (state: GlobalState) =>
  state.residentProfile;

export const isResidentStage = createSelector(
  getCurrentResidentState,
  (residentState) => {
    return residentState?.residentInfo?.id ?? null;
  },
);
